import React, { useState, useEffect } from 'react';
import { Save, Key, Sparkles, FolderOpen } from 'lucide-react';
import { db } from '../../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const Settings: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [savingAuth, setSavingAuth] = useState(false);
    const [savingThemes, setSavingThemes] = useState(false);
    const [savingCategories, setSavingCategories] = useState(false);
    const [asaasApiKey, setAsaasApiKey] = useState('');
    const [aiThemes, setAiThemes] = useState('');
    const [gameCategories, setGameCategories] = useState('');

    useEffect(() => {
        if (!db) return;

        const loadSettings = async () => {
            try {
                const docRef = doc(db, 'settings', 'integrations');
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    setAsaasApiKey(snap.data().asaas_api_key || '');
                }

                const aiRef = doc(db, 'settings', 'ai_prompts');
                const aiSnap = await getDoc(aiRef);
                if (aiSnap.exists() && aiSnap.data().themes) {
                    setAiThemes(aiSnap.data().themes.join('\n'));
                } else {
                    setAiThemes("Traição na alta sociedade decadente\nCorrupção no departamento de polícia\nContrabando na zona portuária");
                }

                const gameRef = doc(db, 'settings', 'gameplay');
                const gameSnap = await getDoc(gameRef);
                if (gameSnap.exists() && gameSnap.data().categories) {
                    setGameCategories(gameSnap.data().categories.join('\n'));
                } else {
                    setGameCategories("Casos Recentes\nMistérios Longos\nCasos Sobrenaturais");
                }
            } catch (error) {
                console.error("Erro ao carregar configurações:", error);
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
    }, []);

    const handleSaveAuth = async () => {
        if (!db) return;
        setSavingAuth(true);
        try {
            await setDoc(doc(db, 'settings', 'integrations'), {
                asaas_api_key: asaasApiKey,
                updatedAt: new Date()
            }, { merge: true });
            alert("Chaves salvas com sucesso!");
        } catch (error) {
            console.error("Erro ao salvar integrações:", error);
            alert("Erro ao salvar integrações.");
        } finally {
            setSavingAuth(false);
        }
    };

    const handleSaveThemes = async () => {
        if (!db) return;
        setSavingThemes(true);
        try {
            const themesArr = aiThemes.split('\n').filter(t => t.trim() !== '');
            await setDoc(doc(db, 'settings', 'ai_prompts'), { themes: themesArr }, { merge: true });
            alert("Sementes de IA salvas com sucesso!");
        } catch (error) {
            console.error("Erro ao salvar promps de IA:", error);
            alert("Erro ao salvar sementes de IA.");
        } finally {
            setSavingThemes(false);
        }
    };

    const handleSaveCategories = async () => {
        if (!db) return;
        setSavingCategories(true);
        try {
            const catArr = gameCategories.split('\n').filter(t => t.trim() !== '');
            await setDoc(doc(db, 'settings', 'gameplay'), { categories: catArr }, { merge: true });
            alert("Sessões/Categorias salvas com sucesso!");
        } catch (error) {
            console.error("Erro ao salvar categorias do jogo:", error);
            alert("Erro ao salvar categorias.");
        } finally {
            setSavingCategories(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Carregando configurações...</div>;

    return (
        <div className="animate-in fade-in duration-500 max-w-4xl">
            <h1 className="text-3xl font-bold text-gray-800 mb-8">Configurações do Sistema</h1>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center gap-3">
                    <Key className="text-blue-500" />
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Integração: Asaas (PIX)</h2>
                        <p className="text-xs text-gray-500">Configuração de chaves API para geração instantânea de PIX via Cloud Functions.</p>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-800">
                        <strong>Nota Técnica:</strong> Esta API Key será buscada pela nossa Firebase Function (backend) quando um jogador clicar em Comprar. Todo o processo de criação de fatura e recebimento do webhook passará criptografado longe do frontend.
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Asaas API Key (Produção/Sandbox)</label>
                        <input
                            type="password"
                            value={asaasApiKey}
                            onChange={e => setAsaasApiKey(e.target.value)}
                            placeholder="$aact_..."
                            className="w-full text-sm font-mono border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">Sua chave de acesso obtida no painel do Asaas.</p>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-gray-100">
                        <button
                            onClick={handleSaveAuth}
                            disabled={savingAuth}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded flex items-center gap-2 transition-colors disabled:opacity-50"
                        >
                            <Save size={18} /> {savingAuth ? 'SALVANDO...' : 'SALVAR CHAVES'}
                        </button>
                    </div>
                </div>
            </div>

            {/* AI Prompts Settings */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
                <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-center gap-3">
                    <Sparkles className="text-amber-500" />
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Sementes de Inspiração (Geração IA)</h2>
                        <p className="text-xs text-gray-500">Adicione uma ideia por linha. O Gemini fará um sorteio aleatório ao gerar um novo caso.</p>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    <textarea
                        className="w-full text-sm font-mono text-gray-700 border border-gray-300 rounded p-4 focus:ring-2 focus:ring-amber-500 outline-none resize-y"
                        rows={6}
                        value={aiThemes}
                        onChange={e => setAiThemes(e.target.value)}
                        placeholder="Ex: Assalto a trem pagador\nMistério no farol abandonado..."
                    />

                    <div className="flex justify-end pt-4 border-t border-gray-100">
                        <button
                            onClick={handleSaveThemes}
                            disabled={savingThemes}
                            className="bg-amber-600 hover:bg-amber-700 text-white font-bold py-2 px-6 rounded flex items-center gap-2 transition-colors disabled:opacity-50"
                        >
                            <Save size={18} /> {savingThemes ? 'SALVANDO...' : 'SALVAR SEMENTES'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Gameplay Categories Settings */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
                <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-4 flex items-center gap-3">
                    <FolderOpen className="text-emerald-500" />
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Sessões & Categorias (App do Jogador)</h2>
                        <p className="text-xs text-gray-500">Adicione uma categoria por linha (ex: Sobrenatural, Roubo). Elas vão se tornar as fileiras (estilo Netflix) na tela inicial dos jogadores.</p>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    <textarea
                        className="w-full text-sm font-mono text-gray-700 border border-gray-300 rounded p-4 focus:ring-2 focus:ring-emerald-500 outline-none resize-y"
                        rows={4}
                        value={gameCategories}
                        onChange={e => setGameCategories(e.target.value)}
                        placeholder="Ex: Casos Curtos\nMistérios Complexos\nRoubo a Banco"
                    />

                    <div className="flex justify-end pt-4 border-t border-gray-100">
                        <button
                            onClick={handleSaveCategories}
                            disabled={savingCategories}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-6 rounded flex items-center gap-2 transition-colors disabled:opacity-50"
                        >
                            <Save size={18} /> {savingCategories ? 'SALVANDO...' : 'SALVAR CATEGORIAS'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
